import * as React from 'react';
import {
  classes,
  createId,
  HelperText,
  OutlinedInput,
  useSlug,
} from '@bloomberg/media-ui-core';
import { ChevronDown } from '@bloomberg/media-ui-icons';
import type { StateChangeOptions } from 'downshift';
import Downshift from 'downshift';

import { DropdownOption } from '../DropdownOption/index';
import {
  DropdownOption as DropdownOptionType,
  InnerProps,
  Props,
} from '../props';

import styles from './styles.module.css';

// Inner component
// We are forwarding the ref here so that Downshift
// can attach correctly
const Dropdown = React.forwardRef<HTMLDivElement, InnerProps>(function Dropdown(
  props,
  ref
) {
  const {
    disabled,
    label,
    name,
    options,
    placeholder = '',
    noOptionsText = 'No options available',
    open,
    renderProps,
    selectedItem,
    setOpen,
    setSelectedItem,
    setTempValue,
    filterOptions: userProvidedFilterFunction,
    size,
    tempValue,
    inputProps,
    labelProps,
    required = false,
    unstyled = false,
    error,
    helperText,
    id,
  } = props;

  const {
    getInputProps,
    getLabelProps,
    getRootProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    inputValue,
  } = renderProps;

  const defaultOptionsToRender = React.useMemo(() => {
    return options.filter((item) => {
      // if we have a chosen item and the input is same value
      // show all results for next click
      if (
        selectedItem &&
        (selectedItem?.displayValue === inputValue ||
          selectedItem?.value === inputValue)
      ) {
        return true;
      }

      // if we have no input, return all results
      if (!inputValue) {
        return true;
      }

      // prefer displayValue over value if provided
      const matcher = item.displayValue || item.value;

      // otherwise, filter results
      return matcher.toLowerCase().includes(inputValue.toLowerCase());
    });
  }, [open, options, inputValue, selectedItem]);

  // rendered list
  const renderOptions = userProvidedFilterFunction
    ? userProvidedFilterFunction(inputValue)
    : defaultOptionsToRender;

  const menuProps = {
    ...getMenuProps(),
    'aria-labelledby': `${id}-menu`,
  };

  return (
    <>
      <div
        className={classes([styles['input-container']], { unstyled })}
        ref={ref}
      >
        <OutlinedInput
          unstyled={unstyled}
          disabled={disabled}
          label={label}
          id={id}
          size={size}
          name={`${name}-input`}
          placeholder={placeholder}
          value={getInputProps<DropdownOptionType>().value || ''}
          required={required}
          error={error}
          helperText={helperText}
          labelProps={{
            ...getLabelProps(),
            ...labelProps,
          }}
          inputProps={{
            ...inputProps,
            autoComplete: 'off',
            'aria-labelledby': `${id}-label`,
            // manually ensure that downshift
            // methods are called
            onBlur: (ev: React.FocusEvent<HTMLInputElement>) => {
              ev.persist();
              // all the below methods on getInputProps can
              // be optionally undefined
              const { onBlur } = getInputProps();

              if (onBlur) {
                onBlur(ev);
              }

              // we need to manually call inputProps here
              // since we are overriding the onBlur
              if (inputProps?.onBlur) {
                inputProps.onBlur(ev);
              }
            },
            onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
              const { onChange } = getInputProps();

              ev.persist();

              // store the value in tmp state
              // this is mostly used to run
              // checks on IF we have an active value
              setTempValue(ev.target.value);

              if (onChange) {
                onChange(ev);
              }

              // make sure results are showing to filter on
              setOpen(true);

              // handle possible inputProps event
              if (inputProps?.onChange) {
                inputProps.onChange(ev);
              }
            },
            onFocus: (ev) => {
              setOpen(true);
              if (inputProps?.onFocus) {
                inputProps.onFocus(ev);
              }
            },
            onKeyDown: (ev: React.KeyboardEvent<HTMLInputElement>) => {
              const { onKeyDown } = getInputProps();

              if (onKeyDown) {
                onKeyDown(ev);
              }

              if (ev.key === 'Enter') {
                if (!tempValue) {
                  setSelectedItem(null);
                }
              }

              if (ev.key === 'Escape') {
                setOpen(false);
              }

              if (inputProps?.onKeyDown) {
                inputProps.onKeyDown(ev);
              }
            },
          }}
          {...getRootProps({}, { suppressRefError: true })}
        />
        <button
          aria-label="Toggle Menu"
          type="button"
          disabled={disabled}
          className={classes(
            [
              styles['icon-button'],
              {
                [styles.error]: error,
              },
            ],
            { unstyled }
          )}
          {...getToggleButtonProps()}
          onClick={(ev: React.MouseEvent<HTMLButtonElement>) => {
            setOpen((o) => !o);
            getToggleButtonProps().onClick(ev);
          }}
        >
          <span
            className={classes([styles.chevron, { [styles.open]: open }], {
              unstyled,
            })}
          >
            <ChevronDown />
          </span>
        </button>
      </div>

      <ul
        className={classes(
          [
            styles.menu,
            {
              [styles['menu-open']]: open,
              // helper classes, not used for styling
              open,
              closed: !open,
            },
          ],
          props
        )}
        {...menuProps}
      >
        {open && renderOptions.length === 0 && noOptionsText && (
          <li
            className={classes(
              [styles.item, styles.empty, { [styles.small]: size === 'small' }],
              { unstyled }
            )}
          >
            {noOptionsText}
          </li>
        )}
        {renderOptions.length > 0 &&
          renderOptions.map((item, index) => (
            <DropdownOption
              size={size}
              isSelected={item.value === selectedItem?.value}
              isHighlighted={index === highlightedIndex}
              key={`${item.value}${index}`}
              displayValue={item.displayValue}
              value={item.value}
              {...getItemProps({ item, index })}
            />
          ))}
      </ul>
    </>
  );
});

export const OutlinedDropdown = React.forwardRef<HTMLDivElement, Props>(
  function Component(props, ref) {
    const {
      disabled = false,
      name: userProvidedName,
      id: userProvidedId,
      inputProps,
      labelProps,
      noOptionsText,
      options = [],
      onChange: onChangeProp,
      onTextChange,
      filterOptions,
      placeholder = '',
      label = '',
      value,
      defaultValue,
      required,
      size = 'default',
      error,
      helperText,
    } = props;

    // determines whether or not to show results
    // this extends the base downshift behavior
    // to act more similarly to material-ui, where the drawer shows
    // all results after the focus is blurred.
    const [open, setOpen] = React.useState(false);
    const stableId = React.useRef(createId());

    // create a unique id for inputs
    const id =
      userProvidedId ||
      inputProps?.id ||
      userProvidedName ||
      label ||
      stableId.current;

    // store a reference to the current input value.
    // used to determine whether or not we should clear
    // all values on Enter key or blur if the user deleted inputs
    const [tempValue, setTempValue] = React.useState(
      (value || defaultValue)?.value || ''
    );

    // item user actually chose...
    // either via click, enter, or blur on valid value
    const [selectedItem, setSelectedItem] =
      React.useState<DropdownOptionType | null>(value || defaultValue || null);

    const name = userProvidedName || 'OutlinedDropdown';
    // stable name for data-component attribute
    const [slugName] = useSlug(name);

    // `handleLocalChange` fires only when a new value is selected
    const handleLocalChange = React.useCallback(
      (nextSelectedItem: DropdownOptionType | null): void => {
        // if user passed in an onChange prop, run it
        if (onChangeProp) {
          onChangeProp(nextSelectedItem);
        }

        // set the value in local state
        setSelectedItem(nextSelectedItem);

        // and make sure to set the temp val for equality
        // checking later
        setTempValue(nextSelectedItem?.value || '');

        // reset local state
        setOpen(false);
      },
      [onChangeProp]
    );

    // Watch for state changes on downshift
    // and close the menu on any blur events
    const handleStateChange = React.useCallback(
      (state: StateChangeOptions<DropdownOptionType>): void => {
        // close the drawer on the following events
        if (
          state.type === Downshift.stateChangeTypes.clickItem ||
          state.type === Downshift.stateChangeTypes.mouseUp ||
          state.type === Downshift.stateChangeTypes.blurButton ||
          state.type === Downshift.stateChangeTypes.blurInput
        ) {
          setOpen(false);
        }
      },
      [onTextChange]
    );

    const handleInputValueChange = React.useCallback(
      (val: string) => {
        if (onTextChange) {
          onTextChange(val);
        }
      },
      [onTextChange]
    );

    function inputStringToRenderAfterSelection(
      item: DropdownOptionType | null
    ) {
      return item?.displayValue || item?.value || '';
    }

    // main component return
    return (
      <div
        ref={ref}
        data-component={slugName}
        id={`select-box-${label}`}
        className={classes([styles.wrapper], props)}
      >
        <Downshift
          inputId={id}
          labelId={`${id}-label`}
          menuId={`${id}-menu`}
          onChange={handleLocalChange}
          isOpen={open}
          initialInputValue={defaultValue?.displayValue || defaultValue?.value}
          onInputValueChange={handleInputValueChange}
          inputValue={value?.displayValue || value?.value}
          onStateChange={handleStateChange}
          itemToString={inputStringToRenderAfterSelection}
        >
          {(renderProps) => (
            <Dropdown
              size={size}
              disabled={disabled}
              renderProps={renderProps}
              name={name}
              label={label}
              id={id}
              placeholder={placeholder}
              options={options}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              setOpen={setOpen}
              open={open}
              noOptionsText={noOptionsText}
              tempValue={tempValue}
              setTempValue={setTempValue}
              filterOptions={filterOptions}
              unstyled={props.unstyled}
              inputProps={inputProps}
              labelProps={labelProps}
              required={required}
              error={error}
              // we are marking the input helpertext
              // as undefined so that it does NOT
              // render. We are instead
              // rendering it directly in the dropdown
              helperText={undefined}
              {...renderProps.getRootProps()}
            />
          )}
        </Downshift>
        {helperText && <HelperText text={helperText} error={error} />}
      </div>
    );
  }
);
